import {Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';

import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {AlertController, Platform, ToastController} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import {environment} from '../../environments/environment';
import {Store} from '@ngrx/store';
import {AuthState} from '../store/auth/auth.reducer';
import {LoginCallBackAction} from '../store/auth/auth.actions';
import {Router} from '@angular/router';

/** Pass untouched request through to the next request handler. */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  constructor(
    public toastController: ToastController,
    public alertController: AlertController,
    public ts: TranslateService,
    private platform: Platform,
    private router: Router,
    private store: Store<{ authStateReducer: AuthState }>
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authReq = req.clone();

    return next.handle(authReq).pipe(
      tap(
        // Succeeds when there is a response; ignore other events
        () => {},
        // Operation failed; error is an HttpErrorResponse
        async (error) => {
          console.log('error', error);
          await this.handleHttpError(req, error);
        }
      )
    );
  }

  private async handleHttpError(req: HttpRequest<any>, error) {
    if (this.isBackgroundRequest(req)) {
      return;
    }

    const msg = error.error?.message || error.message;
    switch (error.status) {
      case 0:
      case 408:
        this.presentWarning('Connection error, please check your internet connection and try again.');
        return;
      case 401:
        this.presentLogoutConfirm(error);
        this.store.dispatch(new LoginCallBackAction());
        return;
      case 400:
        this.presentConfirm('Something went wrong', msg, error);
        return;
      case 403:
        this.presentConfirm('Forbidden', msg, error);
        return;
      case 405:
        this.presentConfirm('Not Allowed', msg, error);
        return;
      case 404:
        this.handleNotFound(error, msg);
        return;
      default:
        if (error.error?.details === 'Invalid pass code!') {
          return;
        }
        this.presentToast('Something went wrong.', error);
        return;
    }
  }

  private isBackgroundRequest(req: HttpRequest<any>): boolean {
    return Boolean(
      req.body?.silent ||
      req.params.get('silent') ||
      req.headers.get('errors') === 'silent'
    );
  }

  private handleNotFound(error, msg: string) {
    if (error.url.indexOf('me') > -1) {
      if (this.isHybridMobile()) {
        this.presentToast('User is not associated with any company.');
      } else {
        this.presentToast('User is not associated with any company, redirecting to admin app..');
        setTimeout(() => window.location.href = environment.adminUrl, 2500);
      }
      return;
    }

    this.presentConfirm('Not Found', msg, error);
  }

  private isHybridMobile(): boolean {
    return this.platform.is('hybrid') && (this.platform.is('android') || this.platform.is('ios'));
  }

  async presentToast(text: string, error?) {
    const config: any = {
      header: this.ts.instant(text),
      message: this.ts.instant(error?.message),
      position: 'top',
      color: 'danger',
      duration: 3000,
      buttons: [{
        text: '×',
        role: 'cancel',
        handler: () => {
          toast.dismiss();
        }
      }]
    };
    if (environment.name !== 'production') {
      config.header = `Status: ${error?.status}`;
      config.message += ` [ ${error?.message || error.url} ]`;
      config.duration = 6000;
    }
    const toast = await this.toastController.create(config);
    toast.present();
  }

  async presentWarning(text: string) {
    const toast = await this.toastController.create({
      message: this.ts.instant(text),
      position: 'top',
      color: 'warning',
      duration: 3000,
      buttons: [{
        text: '×',
        role: 'cancel',
        handler: () => {
          toast.dismiss();
        }
      }]
    });
    toast.present();
  }

  async presentConfirm(header: string, message: string, error?) {
    if (environment.name !== 'production') {
      message += ` [dev: ${error?.status} ${error?.message || error.url}]`
    }

    const confirm = await this.alertController.create({
      header: this.ts.instant(header),
      message: this.ts.instant(message),
      // message: this.ts.instant(message),
      buttons: [{
        text: this.ts.instant('Ok'),
        cssClass: 'secondary',
        role: 'cancel',
        handler: () => { }
      }]
    });
    confirm.present();
  }

  async presentLogoutConfirm(error?) {
    const message = 'There is an unexpected temporary issue, please try later or logout and re-login.';
    let adds = '';
    if (environment.name !== 'production') {
      adds += ` [dev: ${error?.status} ${error?.message || error?.url}]`
    }

    const confirm = await this.alertController.create({
      header: this.ts.instant('Not Authorized'),
      message: this.ts.instant(message) + adds,
      buttons: [{
        text: this.ts.instant('Try later'),
        cssClass: 'secondary',
        role: 'cancel',
        handler: () => {}
      }, {
        text: this.ts.instant('Log out'),
        cssClass: 'secondary',
        handler: () => {
          this.router.navigate(['/settings-menu'], {
            queryParams: {
              forceLogout: true
            }
          });
        }
      }]
    });
    confirm.present();
  }
}
